import { createContext, useState, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { GetProjectData, PROJECT_SYSTEM_TYPE } from 'Infrastructure/Service/ProjectService'
import { cleanStores } from 'Application/LocalStorage/IndexedDBHandler'
import { projectManager, validateManager, nullManager } from 'Application/LocalStorage/projectAdapter'

import { isConnected, getLiveAccess } from 'Infrastructure/Service/AccountService'
import { initPhaseSystem, setPhase, setError, INIT_PHASES } from 'Domain/Scene/Manager/InitializationOrchestrator'

/*****************************************************************
 *  Context
 * ***************************************************************/

export const ProjectContext = createContext()

/**
 * Give context project informations
 * @param {*} props
 * @returns
 */
export const ProjectContextProvider = props => {

    const buildCaseLogoUrl = (json) => {
        const projectRef = json?.sk || json?.designation
        if (projectRef) {
            return `${process.env.REACT_APP_U1_VIEWERSVC_API}project/${projectRef}/logo?dark=true`
        }
        return `${process.env.REACT_APP_U1_VIEWERSVC_API}company/logo?dark=true`
    }

    const extractTokenFromUrl = () => {
        const queryParams = new URLSearchParams(window.location.search);
        const queryToken = queryParams.get('token');
        
        if (queryToken) return queryToken;
        
        let pathname = window.location.pathname.split('/').filter(Boolean)[0];
        
        if (pathname && pathname.includes('&')) {
            const uuidMatch = pathname.match(/^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/);
            if (uuidMatch) {
                const actionMatch = pathname.match(/&action=([^&]+)/);
                if (actionMatch) {
                    const correctUrl = `${window.location.origin}/${uuidMatch[1]}?action=${actionMatch[1]}`;
                    window.location.replace(correctUrl);
                    return null;
                }
                return uuidMatch[1];
            }
        }
        
        if (pathname && pathname.length === 36) {
            return pathname;
        }
        
        return null;
    };

    const queryParams = new URLSearchParams(window.location.search),
        dispatch = useDispatch(),
        legacyBase64Param = queryParams.get('p'),
        tokenParam = extractTokenFromUrl(),
        actionParam = queryParams.get('action') ?? null,
        userParam = queryParams.get('u') ?? false,
        invitationParam = queryParams.get('invitation') ?? null,
        decodeBase64 = useCallback(string => new Promise( resolve => resolve ( decodeURIComponent(escape(window.atob(legacyBase64Param.substring( string )))) ) ) , [legacyBase64Param] ) ,
        getSubject = chain => ( [ 'project', 'validation', 'refuse' ].includes(chain[0]) && typeof parseInt(chain[1]) === 'number' ) ? { type: chain[0], ref: chain[1] } : { type: null } ,
        [project, setProject] = useState({ type: null })

    const finishValidationView = (json, caseLogoUrl) => {
        setProject({ ...validateManager(json), ref: json, caseLogoUrl })
        setPhase(INIT_PHASES.COMPLETE)
    }

    useEffect( () => {
        initPhaseSystem()
        setPhase(INIT_PHASES.STORE_READY)

        isConnected().then(connected => {
            if (connected) {
                dispatch({ type: "USER_CONNECTED", payload: true })
                getLiveAccess()
                    .then(hasAccess => dispatch({ type: 'SET_LIVE_ACCESS', payload: hasAccess }))
                    .catch(() => dispatch({ type: 'SET_LIVE_ACCESS', payload: false }))
            }
        });

        cleanStores()

        // Mode LEGACY: utilisation directe du paramètre ?p= sans token
        if (PROJECT_SYSTEM_TYPE === 'legacy' && legacyBase64Param) {
            decodeBase64(0).then(decoded => {
                const chain = decoded.split('-');
                const subject = getSubject(chain);
                
                if (subject.type === 'project' && subject.ref) {
                    setPhase(INIT_PHASES.PROJECT_FETCHING);
                    
                    GetProjectData(subject.ref)
                        .then(response => response.json())
                        .then(json => {
                            const caseLogoUrl = buildCaseLogoUrl(json)

                            setProject(prev => ({ ...prev, ref: json, caseLogoUrl }))
                            projectManager(json).then(dto => setProject({...dto, ref: json, caseLogoUrl}));
                        })
                        .catch(error => {
                            setError('request not valid');
                            console.error("Error fetching project data (legacy mode):", error);
                        });
                } else if (subject.type === 'validation' || subject.type === 'refuse') {
                    // Pour validation/refus en mode legacy
                    GetProjectData(subject.ref)
                        .then(response => response.json())
                        .then(json => {
                            finishValidationView(json)
                        })
                        .catch(error => {
                            console.error("Error fetching validation data (legacy mode):", error);
                        });
                } else {
                    setProject(nullManager());
                }
            });
            return;
        }

        // Mode NEW: gestion avec token UUID (migration + nouveau système)
        const ENABLE_LEGACY_MIGRATION = true;
        
        if (ENABLE_LEGACY_MIGRATION && legacyBase64Param && !tokenParam) {
            decodeBase64(0).then(decoded => {
                const chain = decoded.split('-');
                const subject = getSubject(chain);
                
                if (subject.type === 'project' && subject.ref) {
                    fetch(`${process.env.REACT_APP_U1_VIEWERSVC_API}project/${subject.ref}/token`, {
                        method: 'POST',
                        headers: { 'Accept': 'application/json' }
                    })
                    .then(res => res.json())
                    .then(data => {
                        if (data.token) {
                            const params = new URLSearchParams(window.location.search);
                            params.delete('p');
                            
                            const queryString = params.toString() ? '?' + params.toString() : '';
                            const newUrl = `${window.location.origin}/${data.token}${queryString}`;
                            
                            window.location.replace(newUrl);
                        }
                    })
                    .catch(err => {
                        console.error('Échec de la migration URL:', err);
                    });
                }
            });
            
            return;
        } else if (!ENABLE_LEGACY_MIGRATION && legacyBase64Param && !tokenParam) {
            console.error('Format URL legacy (?p=) n\'est plus supporté');
            return;
        }

        if (tokenParam) {
            setPhase(INIT_PHASES.PROJECT_FETCHING)
            
            GetProjectData( null, tokenParam )
                .then( response => {
                    response.json().then( json =>  {
                        if (json && json.Reference && json.Reference.hash) {
                            const caseLogoUrl = buildCaseLogoUrl(json)

                            setProject(prev => ({ ...prev, ref: json, caseLogoUrl }))
                            
                            if (actionParam === 'validate') {
                                finishValidationView(json, caseLogoUrl)
                            } else {
                                projectManager(json).then( dto => setProject({...dto, ref: json, caseLogoUrl}) )
                            }
                        } else {
                            console.error("Project data incomplete or missing Reference:", json)
                            setError('project data not valid')
                        }
                    })
                })
                .catch( error => {
                    setError('request not valid')
                    console.error("Error fetching data with token: ", error)
                 } )
                .finally()
        } else {
            // No token access denied
            console.error("Access denied: No token provided")
            window.location.replace('/dashboard')
        }
    }, [decodeBase64, dispatch, userParam, invitationParam, tokenParam, actionParam, legacyBase64Param ] )

    return (
        <ProjectContext.Provider value={project}>
            {props.children}
        </ProjectContext.Provider>
    )
}
